import { useState, useEffect } from "react";
import { Search, Filter } from "lucide-react";
import { SpeakerCard } from "./SpeakerCard";
import { SpeakerModal } from "./SpeakerModal";
import { SpeakersLoadingState } from "./SpeakersLoadingState";
import { BackgroundSelector } from "../BackgroundSelector";
import { Speaker } from "./types";
import { useSpeakers } from "../../hooks/useSpeakers";
import { useEventContext } from "../../contexts/EventContext";
import { useEventDetails } from '../../../hooks/useEventDetails';
import { trackInteraction, trackSearch } from '../../utils/gtm';
export const SpeakersPage = () => {
  const { eventSlug } = useEventContext();
  const { data: speakersResponse, isLoading, error } = useSpeakers(eventSlug);
  const { data: eventDetails } = useEventDetails(eventSlug);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [background, setBackground] = useState("bg-gray-50");

  // Update page title with custom navigation name
  useEffect(() => {
    if (eventDetails) {
      const speakersName = eventDetails.eventOptions?.speakersName || 'Speakers';
      document.title = `${speakersName} - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  const speakers = speakersResponse?.data.speakers || [];
  const topics = speakersResponse?.data.topics || [];

  const filteredSpeakers = speakers.filter(speaker => {
    const matchesSearch = speaker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         speaker.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         speaker.bio?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTopics = selectedTopics.length === 0 ||
                         speaker.topics.some(topic => selectedTopics.includes(topic));
    return matchesSearch && matchesTopics;
  });

  // Track search when search term changes (with debounce effect)
  useEffect(() => {
    if (searchTerm.trim()) {
      const delayedSearch = setTimeout(() => {
        trackSearch(searchTerm, filteredSpeakers.length);
        trackInteraction('speakers_search', {
          search_term: searchTerm,
          result_count: filteredSpeakers.length,
          event_slug: eventSlug,
          page: 'speakers'
        });
      }, 500); // 500ms debounce

      return () => clearTimeout(delayedSearch);
    }
  }, [searchTerm, filteredSpeakers.length, eventSlug]);

  // Track topic filter changes
  useEffect(() => {
    if (selectedTopics.length > 0) {
      trackInteraction('speakers_filter', {
        filter_type: 'topic',
        selected_topics: selectedTopics,
        result_count: filteredSpeakers.length,
        event_slug: eventSlug
      });
    }
  }, [selectedTopics, filteredSpeakers.length, eventSlug]);

  if (isLoading) {
    return (
      <div className={`min-h-[calc(100vh-64px)] ${background}`}>
        <BackgroundSelector onSelect={setBackground} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <SpeakersLoadingState />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-[calc(100vh-64px)] ${background}`}>
        <BackgroundSelector onSelect={setBackground} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-center py-12">
              <p className="text-red-600">Failed to load {(eventDetails?.eventOptions?.speakersName || 'speakers').toLowerCase()}. Please try again later.</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return <div className={`min-h-[calc(100vh-64px)]`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{eventDetails?.eventOptions?.speakersName || 'Speakers'}</h1>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-[50%] -translate-y-[50%] text-gray-400 pointer-events-none" size={20} />
                <input type="text" placeholder={`Search ${(eventDetails?.eventOptions?.speakersName || 'speakers').toLowerCase()}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-600">
                <Filter size={18} />
                <span className="text-sm font-medium">Topics</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {topics.map(topic => (
                  <button
                    key={topic}
                    onClick={() => setSelectedTopics(prev =>
                      prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
                    )}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors
                      ${selectedTopics.includes(topic) ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSpeakers.map(speaker => (
                <SpeakerCard 
                  key={speaker.id} 
                  speaker={speaker} 
                  onClick={() => {
                    // Track speaker profile view
                    trackInteraction('speaker_profile_view', {
                      speaker_name: speaker.name,
                      speaker_id: speaker.id,
                      speaker_company: speaker.company,
                      event_slug: eventSlug,
                      interaction_source: 'speakers_page'
                    });
                    setSelectedSpeaker(speaker);
                  }} 
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {selectedSpeaker && <SpeakerModal speaker={selectedSpeaker} onClose={() => setSelectedSpeaker(null)} />}
    </div>;
};
